import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { MdBugReport, MdAdd, MdClose } from "react-icons/md";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  icon: {
    color: "#2196f3",
    width: 24,
    height: 24,
  },
  content: {
    padding: theme.spacing(3),
  },
  variableChip: {
    margin: theme.spacing(0.5),
    fontFamily: "monospace",
  },
  previewBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    fontFamily: "monospace",
    fontSize: "12px",
    whiteSpace: "pre-wrap",
    maxHeight: "200px",
    overflow: "auto",
  },
  helperText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  suggestedVars: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  suggestedChip: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const logLevels = [
  { value: "debug", label: "Debug", color: "#9e9e9e", description: "Detalhes de depuração" },
  { value: "info", label: "Info", color: "#2196f3", description: "Informações gerais" },
  { value: "warn", label: "Warning", color: "#ff9800", description: "Avisos importantes" },
  { value: "error", label: "Error", color: "#f44336", description: "Erros críticos" },
];

const suggestedVariables = [
  { name: "nome", description: "Nome do contato" },
  { name: "numero", description: "Número do contato" },
  { name: "email", description: "Email do contato" },
  { name: "apiResponse", description: "Resposta da última API" },
];

const FlowBuilderAddDebugModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    label: "",
    logLevel: "info",
    variables: [],
    message: "",
    logAllVariables: false,
  });
  const [newVariable, setNewVariable] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open === "edit" && data?.data) {
      setFormData({
        label: data.data.label || "",
        logLevel: data.data.logLevel || "info",
        variables: data.data.variables || [],
        message: data.data.message || "",
        logAllVariables: data.data.logAllVariables || false,
      });
    } else {
      setFormData({
        label: "",
        logLevel: "info",
        variables: [],
        message: "",
        logAllVariables: false,
      });
    }
    setNewVariable("");
  }, [data, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddVariable = () => {
    if (newVariable && !formData.variables.includes(newVariable)) {
      setFormData((prev) => ({
        ...prev,
        variables: [...prev.variables, newVariable],
      }));
      setNewVariable("");
    }
  };

  const handleRemoveVariable = (varToRemove) => {
    setFormData((prev) => ({
      ...prev,
      variables: prev.variables.filter((v) => v !== varToRemove),
    }));
  };

  const handleAddSuggestedVariable = (varName) => {
    if (!formData.variables.includes(varName)) {
      setFormData((prev) => ({
        ...prev,
        variables: [...prev.variables, varName],
      }));
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    try {
      const nodeData = {
        label: formData.label || "Debug",
        logLevel: formData.logLevel,
        variables: formData.variables,
        message: formData.message,
        logAllVariables: formData.logAllVariables,
      };

      if (open === "edit" && data) {
        onUpdate({
          ...data,
          data: {
            ...data.data,
            ...nodeData,
          },
        });
      } else {
        onSave(nodeData);
      }
    } catch (error) {
      console.error("Erro ao salvar debug:", error);
    } finally {
      setLoading(false);
      close(null);
    }
  };

  const handleClose = () => {
    close(null);
  };

  const selectedLevel = logLevels.find((l) => l.value === formData.logLevel);

  // Gerar preview do log
  const generatePreview = () => {
    const timestamp = new Date().toISOString();
    const level = formData.logLevel.toUpperCase();
    let preview = `[${timestamp}] ${level}: [DEBUG NODE] ${formData.label || "Debug"}\n`;

    if (formData.message) {
      // Simular substituição de variáveis com valores de exemplo
      let messagePreview = formData.message
        .replace(/\$\{nome\}/g, "João Silva")
        .replace(/\$\{numero\}/g, "5511999999999")
        .replace(/\$\{email\}/g, "joao@email.com")
        .replace(/\$\{apiResponse\}/g, "{...}")
        .replace(/\$\{(\w+)\}/g, "<$1>");
      preview += `  ${messagePreview}\n`;
    }

    if (formData.variables.length > 0) {
      preview += `  Variáveis extras:\n`;
      formData.variables.forEach((v) => {
        preview += `    ${v}: <valor em runtime>\n`;
      });
    }

    if (formData.logAllVariables) {
      preview += `  + Todas as variáveis do fluxo\n`;
    }

    return preview;
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <MdBugReport className={classes.icon} />
          <Typography variant="h6">
            {open === "edit" ? "Editar Debug" : "Novo Debug"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.content} dividers>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Label (identificador)
            </Typography>
            <TextField
              fullWidth
              placeholder="Ex: Debug após validação"
              name="label"
              value={formData.label}
              onChange={handleChange}
              variant="outlined"
              size="small"
            />
            <Typography className={classes.helperText}>
              Um nome para identificar este ponto de debug no log
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Nível do Log
            </Typography>
            <TextField
              select
              fullWidth
              name="logLevel"
              value={formData.logLevel}
              onChange={handleChange}
              variant="outlined"
              size="small"
            >
              {logLevels.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: option.color,
                      }}
                    />
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      - {option.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Mensagem do Log
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Ex: Cliente ${nome} (${numero}) chegou neste ponto. Resposta API: ${apiResponse}"
              name="message"
              value={formData.message}
              onChange={handleChange}
              variant="outlined"
              size="small"
              helperText="Use ${variavel} para incluir valores. Ex: ${nome}, ${numero}, ${email}, ${apiResponse}"
            />

            {/* Chips de variáveis sugeridas para inserir na mensagem */}
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
              Clique para inserir na mensagem:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} style={{ marginTop: 4 }}>
              {suggestedVariables.map((sv) => (
                <Chip
                  key={sv.name}
                  label={`\${${sv.name}}`}
                  size="small"
                  onClick={() => {
                    const varText = `\${${sv.name}}`;
                    setFormData((prev) => ({
                      ...prev,
                      message: prev.message ? `${prev.message} ${varText}` : varText,
                    }));
                  }}
                  title={sv.description}
                  style={{ cursor: "pointer", fontFamily: "monospace" }}
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Variáveis adicionais para logar (opcional)
            </Typography>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                placeholder="Nome da variável customizada (ex: minha_variavel)"
                value={newVariable}
                onChange={(e) => setNewVariable(e.target.value)}
                variant="outlined"
                size="small"
                helperText="Adicione variáveis extras que serão mostradas separadamente no log"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddVariable();
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddVariable}
                disabled={!newVariable}
                style={{ minWidth: 50, height: 40 }}
              >
                <MdAdd />
              </Button>
            </Box>

            {/* Variáveis selecionadas */}
            {formData.variables.length > 0 && (
              <Box style={{ marginTop: 12 }}>
                <Typography variant="caption" color="textSecondary">
                  Selecionadas:
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {formData.variables.map((v) => (
                    <Chip
                      key={v}
                      label={`\${${v}}`}
                      size="small"
                      className={classes.variableChip}
                      onDelete={() => handleRemoveVariable(v)}
                      style={{
                        backgroundColor: selectedLevel?.color + "22",
                        borderColor: selectedLevel?.color,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Grid>

          {/* Preview */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Preview do log:
            </Typography>
            <Paper className={classes.previewBox} elevation={0}>
              <code style={{ color: selectedLevel?.color }}>
                {generatePreview()}
              </code>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddDebugModal;
